import { useEffect, useRef } from 'react';
import type { SponsoredPlacement } from '../repositories/sponsoredPlacementRepository';
import { recordSponsoredPlacementClick, recordSponsoredPlacementImpression } from '../repositories/sponsoredPlacementRepository';
import { safeHref } from '../utils/html';

export interface SponsoredPlacementCardProps {
  placement: SponsoredPlacement | {
    id?: string;
    title: string;
    body: string;
    cta_text: string;
    cta_url: string;
    sponsor_name?: string;
    domain?: string | null;
    category?: string | null;
    metadata_text?: string | null;
    background_color?: string | null;
    accent_color?: string | null;
    text_color?: string | null;
    border_color?: string | null;
  };
  preview?: boolean;
  autoFocus?: boolean;
  initiallyExpanded?: boolean;
}

export function SponsoredPlacementCard({ placement, preview = false, autoFocus = false, initiallyExpanded = false }: SponsoredPlacementCardProps) {
  const impressionRecorded = useRef(false);
  const cardRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    if (autoFocus) cardRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  useEffect(() => {
    if (!preview && placement.id && !impressionRecorded.current) {
      impressionRecorded.current = true;
      void recordSponsoredPlacementImpression(placement.id);
    }
  }, [placement.id, preview]);

  const handleClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!preview && placement.id) {
      void recordSponsoredPlacementClick(placement.id);
    }
  };

  const ctaHref = safeHref(placement.cta_url || '#');
  const sponsorBadge = (placement.sponsor_name || 'SPONSOR').toUpperCase();
  const accentColor = placement.accent_color || 'var(--warn, #f0803c)';
  const customBg = placement.background_color || undefined;
  const customBorder = placement.border_color || undefined;
  const customTextColor = placement.text_color || undefined;

  const cardStyle: React.CSSProperties = {
    ...(customBg ? { backgroundColor: customBg } : {}),
    ...(customBorder ? { borderColor: customBorder } : {}),
    ...(customTextColor ? { color: customTextColor } : {}),
  };

  return (
    <details
      ref={cardRef}
      tabIndex={autoFocus ? 0 : undefined}
      open={initiallyExpanded || undefined}
      className="beta-story sponsored-placement-card"
      style={cardStyle}
      data-testid="sponsored-placement-card"
    >
      <summary
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('a, button, input, textarea, select')) {
            return;
          }
        }}
      >
        <span className="beta-story-heading">
          <span className="beta-story-title">
            <span
              className="sponsored-placement-badge"
              style={{ color: accentColor }}
            >
              {sponsorBadge}
            </span>
            {ctaHref && ctaHref !== '#' ? (
              <a
                href={ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleClick}
                style={customTextColor ? { color: customTextColor } : undefined}
              >
                {placement.title}
              </a>
            ) : (
              <span>{placement.title}</span>
            )}
          </span>

          <span className="beta-story-meta">
            {placement.domain && (
              <span className="beta-story-domain">{placement.domain}</span>
            )}
            {placement.category && (
              <span className="sponsored-placement-category">{placement.category}</span>
            )}
            {placement.metadata_text && (
              <span className="beta-story-age">{placement.metadata_text}</span>
            )}
          </span>
        </span>
        <span className="beta-chevron" aria-hidden="true" />
      </summary>

      {placement.body && (
        <div className="beta-story-summary">
          {placement.body}{' '}
          {ctaHref && ctaHref !== '#' && (
            <a
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              className="sponsored-placement-cta"
              onClick={handleClick}
              style={{ color: accentColor }}
            >
              {placement.cta_text || 'Open article →'}
            </a>
          )}
        </div>
      )}
    </details>
  );
}
